import { UserRole } from "../types/user-management.types";

export interface RoleOption {
    label: string;
    value: UserRole;
}

export const roleLabels: Record<UserRole, string> = {
    [UserRole.SUPER_ADMIN]: "Super Admin",
    [UserRole.PROVIDER]: "Provider",
    [UserRole.BILLER]: "Biller",
    [UserRole.CODER]: "Coder",
    [UserRole.AUDITOR]: "Auditor",
    [UserRole.AUDITOR_ADMIN]: "Auditor Admin",
    [UserRole.BILLER_ADMIN]: "Biller Admin",
    [UserRole.CLIENT_SUPPORT]: "Client Support",
    [UserRole.CLIENT]: "Client",
    [UserRole.USER_SUPPORT]: "Support General",
    [UserRole.SUPPORT_GENERAL]: "Support General",
    [UserRole.SUPPORT_ADMIN]: "Support Admin",
};

const standardRoleOptions: RoleOption[] = [
    UserRole.SUPER_ADMIN,
    UserRole.AUDITOR,
    UserRole.AUDITOR_ADMIN,
    UserRole.BILLER,
    UserRole.BILLER_ADMIN,
    UserRole.CLIENT_SUPPORT,
    UserRole.CODER,
    UserRole.PROVIDER,
].map((role) => ({
    value: role,
    label: roleLabels[role],
}));

export function getAssignableRoleOptions(actorRoles: UserRole[]): RoleOption[] {
    if (
        actorRoles.includes(UserRole.SUPPORT_ADMIN) &&
        !actorRoles.includes(UserRole.SUPER_ADMIN)
    ) {
        return [
            ...standardRoleOptions.filter(
                (option) => option.value !== UserRole.SUPER_ADMIN
            ),
            {
                value: UserRole.SUPPORT_GENERAL,
                label: roleLabels[UserRole.SUPPORT_GENERAL],
            },
            {
                value: UserRole.SUPPORT_ADMIN,
                label: roleLabels[UserRole.SUPPORT_ADMIN],
            },
        ];
    }
    if (actorRoles.includes(UserRole.SUPER_ADMIN)) {
        return [
            ...standardRoleOptions,
            {
                value: UserRole.SUPPORT_GENERAL,
                label: roleLabels[UserRole.SUPPORT_GENERAL],
            },
            {
                value: UserRole.SUPPORT_ADMIN,
                label: roleLabels[UserRole.SUPPORT_ADMIN],
            },
        ];
    }
    return standardRoleOptions.filter(
        (option) => option.value !== UserRole.SUPER_ADMIN
    );
}

export function canModifyUserRoles(actorRoles: UserRole[]): boolean {
    return actorRoles.some((role) =>
        [
            UserRole.SUPER_ADMIN,
            UserRole.BILLER_ADMIN,
            UserRole.SUPPORT_ADMIN,
        ].includes(role)
    );
}

export function normalizeUserRoles(roles: string[]): UserRole[] {
    return [
        ...new Set(
            roles.map((role) =>
                role === UserRole.USER_SUPPORT
                    ? UserRole.SUPPORT_GENERAL
                    : (role as UserRole)
            )
        ),
    ];
}

function isSuperOrSupportAdmin(roles: string[]): boolean {
    return normalizeUserRoles(roles).some((role) =>
        [UserRole.SUPER_ADMIN, UserRole.SUPPORT_ADMIN].includes(role)
    );
}

export function isProtectedSupportUser(roles: string[]): boolean {
    return normalizeUserRoles(roles).some((role) =>
        [
            UserRole.SUPER_ADMIN,
            UserRole.SUPPORT_ADMIN,
            UserRole.SUPPORT_GENERAL,
        ].includes(role)
    );
}

function canManageAccount(
    actorRoles: UserRole[],
    targetRoles: string[]
): boolean {
    if (actorRoles.includes(UserRole.SUPER_ADMIN)) {
        return true;
    }
    if (actorRoles.includes(UserRole.SUPPORT_ADMIN)) {
        return !isSuperOrSupportAdmin(targetRoles);
    }
    return !isProtectedSupportUser(targetRoles);
}

export function canManageUserStatus(
    actorRoles: UserRole[],
    targetRoles: string[]
): boolean {
    return canManageAccount(actorRoles, targetRoles);
}

export function canModifyUserRole(
    actorRoles: UserRole[],
    targetRoles: string[]
): boolean {
    return (
        canModifyUserRoles(actorRoles) &&
        canManageAccount(actorRoles, targetRoles)
    );
}

const userNameEditorRoles = [
    UserRole.SUPER_ADMIN,
    UserRole.SUPPORT_ADMIN,
    UserRole.AUDITOR,
    UserRole.AUDITOR_ADMIN,
    UserRole.BILLER_ADMIN,
    UserRole.CLIENT_SUPPORT,
];

export function canEditUserName(
    actorRoles: UserRole[],
    targetRoles: string[]
): boolean {
    return (
        actorRoles.some((role) => userNameEditorRoles.includes(role)) &&
        canManageAccount(actorRoles, targetRoles)
    );
}

export function canResetUserMfa(
    actorRoles: UserRole[],
    targetRoles: string[]
): boolean {
    return (
        [UserRole.SUPER_ADMIN, UserRole.SUPPORT_ADMIN].some((role) =>
            actorRoles.includes(role)
        ) && canManageAccount(actorRoles, targetRoles)
    );
}
